import type { MenuSnapshot, VenueId } from "@orderman/shared";
import { kvGet, kvSet } from "./idb.js";

/**
 * Speisekarte: zuerst aus dem IndexedDB-Cache (offline-fähig), dann — wenn online —
 * per Versionsabgleich beim Server aktualisieren.
 */

const key = (v: VenueId) => `menu:${v}`;

export function loadCachedMenu(venueId: VenueId): Promise<MenuSnapshot | undefined> {
  return kvGet<MenuSnapshot>(key(venueId));
}

/**
 * Holt die Speisekarte vom Server. Liefert die aktualisierte Karte, die unveränderte
 * gecachte (Version stimmt überein), oder `undefined`, wenn der Server nicht wie erwartet
 * geantwortet hat (Netzfehler oder Nicht-2xx — z. B. 404, weil `serverUrl` gerade auf einen
 * Host ohne `/api/menu` zeigt, wie die Cloud same-origin ohne lokalen Server). `undefined`
 * ist ein bewusst ANDERES Ergebnis als "unverändert" — Aufrufer nutzen es, um zu erkennen,
 * dass dieser Server gerade NICHT erreichbar war (z. B. um auf einen Cloud-Fallback
 * umzuschalten), statt das stillschweigend hinter dem alten Cache zu verstecken.
 */
export async function refreshMenu(
  serverUrl: string,
  venueId: VenueId,
): Promise<MenuSnapshot | undefined> {
  const cached = await loadCachedMenu(venueId);
  try {
    const v = cached ? `&version=${cached.version}` : "";
    const res = await fetch(`${serverUrl}/api/menu?venue=${venueId}${v}`);
    if (!res.ok) return undefined;
    const data = (await res.json()) as MenuSnapshot | { upToDate: true };
    if ("upToDate" in data) return cached;
    await kvSet(key(venueId), data);
    return data;
  } catch {
    return undefined; // Server nicht erreichbar (Netzfehler)
  }
}
